import React, { useState, useEffect } from 'react'
import {
  CreditCard,
  Calculator,
  Clock,
  Shield,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  Info,
  Wallet,
  ExternalLink,
  Copy
} from 'lucide-react'
import { mockApiService } from '../services/mockApiService'
import type { MockExchangeRate, MockProviderStatus } from '../services/mockApi'
import { SettlementRailSelector } from './SettlementRailSelector'
import { WalletConnectionForm } from './WalletConnectionForm'

interface PaymentFormData {
  amount: number
  currency: 'HKD' | 'CNY' | 'USD'
  stablecoin: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  provider: 'HashKey' | 'OSL' | 'Moonpay'
  settlementRail: 'FPS' | 'CIPS' | 'e-CNY'
  recipientName: string
  recipientAccount: string
  paymentMethod: 'existing_wallet' | 'on_ramp_purchase'
  onRampProvider?: 'Moonpay' | 'HashKey Exchange' | 'OSL Exchange'
  walletAddress?: string
  walletNetwork?: string
  isWalletConnected?: boolean
}

interface CostCalculation {
  amount: number
  stablecoinAmount: number
  exchangeRate: number
  fxCost: number
  processingFee: number
  totalCost: number
  traditionalCost: number
  savings: number
  savingsPercentage: number
  settlementRail: string
  estimatedTime: string
  onRampCost?: number
  onRampFee?: number
  totalWithOnRamp?: number
}

interface OnRampProvider {
  id: string
  name: string
  logo: string
  supportedStablecoins: string[]
  supportedCountries: string[]
  fees: {
    percentage: number
    fixed: number
  }
  estimatedTime: string
  isAvailable: boolean
}

interface OnRampQuote {
  provider: string
  stablecoin: string
  amount: number
  fiatAmount: number
  fees: number
  totalCost: number
  estimatedTime: string
  exchangeRate: number
}

interface EnhancedPaymentInterfaceProps {
  onPaymentSubmit?: (paymentData: PaymentFormData) => void
  className?: string
  mode?: 'business' | 'customer'
  invoiceData?: {
    invoiceNumber: string
    businessName: string
    amount: number
    currency: string
    description: string
    dueDate: string
  }
}

export const EnhancedPaymentInterface: React.FC<EnhancedPaymentInterfaceProps> = ({
  onPaymentSubmit,
  className = '',
  mode = 'business',
  invoiceData
}) => {
  // Form state
  const [formData, setFormData] = useState<PaymentFormData>({
    amount: invoiceData?.amount || 10000,
    currency: (invoiceData?.currency as 'HKD' | 'CNY' | 'USD') || 'HKD',
    stablecoin: 'USDT',
    provider: 'HashKey',
    settlementRail: 'FPS',
    recipientName: '',
    recipientAccount: '',
    paymentMethod: 'existing_wallet',
    onRampProvider: 'Moonpay',
    walletAddress: undefined,
    walletNetwork: undefined,
    isWalletConnected: false
  })

  // API data state
  const [exchangeRates, setExchangeRates] = useState<MockExchangeRate[]>([])
  const [providerStatuses, setProviderStatuses] = useState<MockProviderStatus[]>([])
  const [costCalculation, setCostCalculation] = useState<CostCalculation | null>(null)
  const [onRampProviders, setOnRampProviders] = useState<OnRampProvider[]>([])
  const [onRampQuote, setOnRampQuote] = useState<OnRampQuote | null>(null)

  // UI state
  const [loading, setLoading] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadingOnRampQuote, setLoadingOnRampQuote] = useState(false)

  // Load initial data
  useEffect(() => {
    loadInitialData()
  }, [])

  // Recalculate costs when form data changes
  useEffect(() => {
    if (formData.amount > 0) {
      calculateCosts()
    }
  }, [formData.amount, formData.stablecoin, formData.settlementRail, formData.paymentMethod, formData.onRampProvider])

  // Load on-ramp quote when payment method changes to on-ramp
  useEffect(() => {
    if (formData.paymentMethod === 'on_ramp_purchase' && formData.amount > 0) {
      loadOnRampQuote()
    }
  }, [formData.paymentMethod, formData.stablecoin, formData.onRampProvider, formData.amount])

  const loadInitialData = async () => {
    try {
      setLoading(true)
      const [ratesResponse, statusResponse, onRampResponse] = await Promise.all([
        mode === 'customer' ? mockApiService.getCustomerExchangeRates() : mockApiService.getExchangeRates(),
        mockApiService.getProviderStatuses(),
        mockApiService.getOnRampProviders()
      ])

      if (ratesResponse.success) {
        setExchangeRates(ratesResponse.data)
      }

      if (statusResponse.success) {
        setProviderStatuses(statusResponse.data)
      }

      if (onRampResponse.success) {
        setOnRampProviders(onRampResponse.data)
      }
    } catch (err) {
      console.error('Failed to load initial data:', err)
      setError('Failed to load payment data. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  const calculateCosts = async () => {
    try {
      setCalculating(true)
      const response = mode === 'customer' ? 
        await mockApiService.calculateCustomerPaymentCost(formData.amount, formData.stablecoin) :
        await mockApiService.calculateCosts(
          formData.amount,
          formData.stablecoin,
        formData.settlementRail
      )

      if (response.success) {
        let calculation
        
        if (mode === 'customer') {
          // Handle customer payment cost response format
          const customerData = response.data
          calculation = {
            amount: customerData.invoiceAmount,
            stablecoinAmount: customerData.stablecoinAmount,
            exchangeRate: customerData.exchangeRate,
            fxCost: customerData.breakdown.fxCost,
            processingFee: customerData.breakdown.processingFee,
            totalCost: customerData.breakdown.totalFees,
            traditionalCost: customerData.comparison.traditionalFees,
            savings: customerData.comparison.savings,
            savingsPercentage: customerData.comparison.savingsPercentage,
            settlementRail: formData.settlementRail,
            estimatedTime: customerData.estimatedConfirmationTime
          }
        } else {
          // Handle business cost calculation response format
          calculation = response.data
        }
        
        // Add on-ramp costs if using on-ramp purchase method
        if (formData.paymentMethod === 'on_ramp_purchase' && onRampQuote) {
          calculation = {
            ...calculation,
            onRampCost: onRampQuote.fiatAmount,
            onRampFee: onRampQuote.fees,
            totalWithOnRamp: onRampQuote.totalCost
          }
        }
        
        setCostCalculation(calculation)
      }
    } catch (err) {
      console.error('Failed to calculate costs:', err)
    } finally {
      setCalculating(false)
    }
  }

  const loadOnRampQuote = async () => {
    if (!formData.onRampProvider || formData.amount <= 0) return

    try {
      setLoadingOnRampQuote(true)
      const response = await mockApiService.getOnRampQuote({
        provider: formData.onRampProvider,
        stablecoin: formData.stablecoin,
        amount: formData.amount,
        currency: formData.currency
      })

      if (response.success) {
        setOnRampQuote(response.data)
      }
    } catch (err) {
      console.error('Failed to load on-ramp quote:', err)
    } finally {
      setLoadingOnRampQuote(false)
    }
  }

  const handleInputChange = (field: keyof PaymentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleWalletConnectionSuccess = (walletAddress: string, networkId: string) => {
    setFormData(prev => ({
      ...prev,
      walletAddress,
      walletNetwork: networkId,
      isWalletConnected: true
    }))
    setError(null)
  }

  const handleWalletConnectionError = (error: string) => {
    setError(`Wallet connection failed: ${error}`)
    setFormData(prev => ({
      ...prev,
      walletAddress: undefined,
      walletNetwork: undefined,
      isWalletConnected: false
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!costCalculation) {
      setError('Please wait for cost calculation to complete')
      return
    }

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      if (onPaymentSubmit) {
        onPaymentSubmit(formData)
      }
    } catch (err) {
      setError('Payment processing failed. Please try again.')
    }
  }

  const getProviderStatus = (provider: string) => {
    return providerStatuses.find(p => p.provider === provider)
  }

  const getStablecoinRate = (stablecoin: string) => {
    return exchangeRates.find(r => r.stablecoin === stablecoin)
  }

  if (loading) {
    return (
      <div className="professional-card">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading payment interface...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`enhanced-payment-interface ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Display for Customer Mode */}
        {mode === 'customer' && invoiceData && (
          <div className="professional-card">
            <div className="professional-card__header">
              <div className="flex items-center gap-3">
                <div className="professional-demo__feature-icon professional-demo__feature-icon--blue">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="professional-card__title">Invoice Details</h3>
                  <p className="professional-card__subtitle">Payment request from {invoiceData.businessName}</p>
                </div>
              </div>
            </div>
            <div className="professional-card__content">
              <div className="enhanced-payment-interface__invoice-details">
                <div className="enhanced-payment-interface__invoice-row">
                  <span className="enhanced-payment-interface__invoice-label">Invoice Number:</span>
                  <span className="enhanced-payment-interface__invoice-value">{invoiceData.invoiceNumber}</span>
                </div>
                <div className="enhanced-payment-interface__invoice-row">
                  <span className="enhanced-payment-interface__invoice-label">Business:</span>
                  <span className="enhanced-payment-interface__invoice-value">{invoiceData.businessName}</span>
                </div>
                <div className="enhanced-payment-interface__invoice-row">
                  <span className="enhanced-payment-interface__invoice-label">Amount:</span>
                  <span className="enhanced-payment-interface__invoice-value enhanced-payment-interface__invoice-amount">
                    {invoiceData.currency} {invoiceData.amount.toLocaleString()}
                  </span>
                </div>
                <div className="enhanced-payment-interface__invoice-row">
                  <span className="enhanced-payment-interface__invoice-label">Description:</span>
                  <span className="enhanced-payment-interface__invoice-value">{invoiceData.description}</span>
                </div>
                <div className="enhanced-payment-interface__invoice-row">
                  <span className="enhanced-payment-interface__invoice-label">Due Date:</span>
                  <span className="enhanced-payment-interface__invoice-value">{invoiceData.dueDate}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Amount Section */}
        {mode === 'business' && (
          <div className="professional-card">
            <div className="professional-card__header">
              <div className="flex items-center gap-3">
                <div className="professional-demo__feature-icon professional-demo__feature-icon--blue">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="professional-card__title">Payment Amount</h3>
                  <p className="professional-card__subtitle">Enter the amount you want to send</p>
                </div>
              </div>
            </div>
            <div className="professional-card__content">
              <div className="professional-grid professional-grid--cols-2">
                <div className="professional-form-group">
                  <label className="professional-form-label">Amount</label>
                  <input
                    type="number"
                    className="professional-form-input"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                    min="1"
                    step="0.01"
                    placeholder="Enter amount"
                  />
                </div>
                <div className="professional-form-group">
                  <label className="professional-form-label">Currency</label>
                  <select
                    className="professional-form-input"
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                  >
                    <option value="HKD">HKD - Hong Kong Dollar</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="USD">USD - US Dollar</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Method Selection for Customer Mode */}
        {mode === 'customer' && (
          <div className="professional-card">
            <div className="professional-card__header">
              <div className="flex items-center gap-3">
                <div className="professional-demo__feature-icon professional-demo__feature-icon--purple">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="professional-card__title">Payment Method</h3>
                  <p className="professional-card__subtitle">Choose how you want to pay</p>
                </div>
              </div>
            </div>
            <div className="professional-card__content">
              <div className="enhanced-payment-interface__payment-methods">
                <button
                  type="button"
                  onClick={() => handleInputChange('paymentMethod', 'existing_wallet')}
                  className={`enhanced-payment-interface__payment-method ${
                    formData.paymentMethod === 'existing_wallet' ? 'enhanced-payment-interface__payment-method--selected' : ''
                  }`}
                >
                  <div className="enhanced-payment-interface__payment-method-icon">
                    <Wallet className="w-6 h-6" />
                  </div>
                  <div className="enhanced-payment-interface__payment-method-details">
                    <div className="enhanced-payment-interface__payment-method-title">Use Existing Wallet</div>
                    <div className="enhanced-payment-interface__payment-method-description">
                      Connect your wallet to pay with existing stablecoins
                    </div>
                  </div>
                  {formData.paymentMethod === 'existing_wallet' && formData.isWalletConnected && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleInputChange('paymentMethod', 'on_ramp_purchase')}
                  className={`enhanced-payment-interface__payment-method ${
                    formData.paymentMethod === 'on_ramp_purchase' ? 'enhanced-payment-interface__payment-method--selected' : ''
                  }`}
                >
                  <div className="enhanced-payment-interface__payment-method-icon">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div className="enhanced-payment-interface__payment-method-details">
                    <div className="enhanced-payment-interface__payment-method-title">Buy Stablecoins</div>
                    <div className="enhanced-payment-interface__payment-method-description">
                      Purchase stablecoins with credit card or bank transfer
                    </div>
                    {formData.paymentMethod === 'on_ramp_purchase' && onRampQuote && (
                      <div className="enhanced-payment-interface__payment-method-status">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span>Est. {onRampQuote.estimatedTime}</span>
                      </div>
                    )}
                  </div>
                  {formData.paymentMethod === 'on_ramp_purchase' && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Wallet Connection Form for Customer Mode */}
        {mode === 'customer' && formData.paymentMethod === 'existing_wallet' && (
          <WalletConnectionForm
            onConnectionSuccess={handleWalletConnectionSuccess}
            onConnectionError={handleWalletConnectionError}
            showNetworkSelection={true}
            preferredNetwork="ethereum"
            className="enhanced-payment-interface__wallet-connection"
          />
        )}

        {/* On-Ramp Provider Selection */}
        {mode === 'customer' && formData.paymentMethod === 'on_ramp_purchase' && (
          <div className="professional-card">
            <div className="professional-card__header">
              <div className="flex items-center gap-3">
                <div className="professional-demo__feature-icon professional-demo__feature-icon--orange">
                  <ExternalLink className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="professional-card__title">On-Ramp Provider</h3>
                  <p className="professional-card__subtitle">Choose your stablecoin purchase provider</p>
                </div>
              </div>
            </div>
            <div className="professional-card__content">
              <div className="enhanced-payment-interface__onramp-providers">
                {onRampProviders.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => handleInputChange('onRampProvider', provider.name)}
                    disabled={!provider.isAvailable}
                    className={`enhanced-payment-interface__onramp-provider ${
                      formData.onRampProvider === provider.name ? 'enhanced-payment-interface__onramp-provider--selected' : ''
                    } ${!provider.isAvailable ? 'enhanced-payment-interface__onramp-provider--disabled' : ''}`}
                  >
                    <div className="enhanced-payment-interface__onramp-provider-header">
                      <div className="enhanced-payment-interface__onramp-provider-name">
                        {provider.name}
                      </div>
                      <div className="enhanced-payment-interface__onramp-provider-fee">
                        {(provider.fees.percentage * 100).toFixed(1)}% + {formData.currency} {provider.fees.fixed}
                      </div>
                    </div>
                    <div className="enhanced-payment-interface__onramp-provider-details">
                      <div className="enhanced-payment-interface__onramp-provider-time">
                        <Clock className="w-3 h-3" />
                        <span>{provider.estimatedTime}</span>
                      </div>
                      <div className="enhanced-payment-interface__onramp-provider-countries">
                        Supports: {provider.supportedCountries.slice(0, 3).join(', ')}
                        {provider.supportedCountries.length > 3 && ` +${provider.supportedCountries.length - 3} more`}
                      </div>
                    </div>
                    {formData.onRampProvider === provider.name && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </button>
                ))}
              </div>

              {/* On-Ramp Quote Display */}
              {onRampQuote && formData.onRampProvider && (
                <div className="enhanced-payment-interface__onramp-quote">
                  <div className="enhanced-payment-interface__onramp-quote-header">
                    <Calculator className="w-4 h-4 text-blue-600" />
                    <span>Purchase Quote from {onRampQuote.provider}</span>
                    {loadingOnRampQuote && (
                      <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                    )}
                  </div>
                  <div className="enhanced-payment-interface__onramp-quote-details">
                    <div className="enhanced-payment-interface__quote-row">
                      <span>You will receive:</span>
                      <span className="enhanced-payment-interface__quote-value">
                        {onRampQuote.amount.toFixed(6)} {onRampQuote.stablecoin}
                      </span>
                    </div>
                    <div className="enhanced-payment-interface__quote-row">
                      <span>Purchase cost:</span>
                      <span>{formData.currency} {onRampQuote.fiatAmount.toFixed(2)}</span>
                    </div>
                    <div className="enhanced-payment-interface__quote-row">
                      <span>Provider fees:</span>
                      <span>{formData.currency} {onRampQuote.fees.toFixed(2)}</span>
                    </div>
                    <div className="enhanced-payment-interface__quote-row enhanced-payment-interface__quote-row--total">
                      <span>Total cost:</span>
                      <span className="enhanced-payment-interface__quote-value">
                        {formData.currency} {onRampQuote.totalCost.toFixed(2)}
                      </span>
                    </div>
                    <div className="enhanced-payment-interface__quote-time">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span>Estimated time: {onRampQuote.estimatedTime}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stablecoin Selection Section */}
        <div className="professional-card">
          <div className="professional-card__header">
            <div className="flex items-center gap-3">
              <div className="professional-demo__feature-icon professional-demo__feature-icon--green">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="professional-card__title">Stablecoin Selection</h3>
                <p className="professional-card__subtitle">Choose your preferred stablecoin</p>
              </div>
            </div>
          </div>
          <div className="professional-card__content">
            <div className="professional-grid professional-grid--cols-2">
              {['USDT', 'USDC', 'HKD₮', 'CNH₮'].map((coin) => {
                const rate = getStablecoinRate(coin)
                const isSelected = formData.stablecoin === coin
                
                return (
                  <button
                    key={coin}
                    type="button"
                    onClick={() => handleInputChange('stablecoin', coin)}
                    className={`enhanced-payment-interface__stablecoin-option ${
                      isSelected ? 'enhanced-payment-interface__stablecoin-option--selected' : ''
                    }`}
                  >
                    <div className="enhanced-payment-interface__stablecoin-icon">
                      {coin}
                    </div>
                    <div className="enhanced-payment-interface__stablecoin-details">
                      <div className="enhanced-payment-interface__stablecoin-name">
                        {coin === 'USDT' ? 'Tether USD' :
                         coin === 'USDC' ? 'USD Coin' :
                         coin === 'HKD₮' ? 'HKD Stablecoin' :
                         'CNH Stablecoin'}
                      </div>
                      {rate && (
                        <div className="enhanced-payment-interface__stablecoin-rate">
                          1 {coin} = {rate.rate.toFixed(4)} {formData.currency}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </button>
                )
              })}
            </div>

            {/* Buy Stablecoins Option for Customer Mode */}
            {mode === 'customer' && formData.paymentMethod === 'existing_wallet' && (
              <div className="enhanced-payment-interface__buy-stablecoins">
                <div className="enhanced-payment-interface__buy-stablecoins-header">
                  <Info className="w-4 h-4 text-blue-600" />
                  <span>Don't have stablecoins? You can buy them instantly</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('paymentMethod', 'on_ramp_purchase')}
                  className="enhanced-payment-interface__buy-option"
                >
                  <div className="enhanced-payment-interface__buy-option-icon">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div className="enhanced-payment-interface__buy-option-details">
                    <div className="enhanced-payment-interface__buy-option-title">Buy Stablecoins</div>
                    <div className="enhanced-payment-interface__buy-option-description">
                      Purchase {formData.stablecoin} with credit card or bank transfer
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Settlement Rail Selection */}
        <div className="professional-card">
          <div className="professional-card__content">
            <SettlementRailSelector
              selectedRail={formData.settlementRail}
              onRailSelect={(rail) => handleInputChange('settlementRail', rail)}
              amount={formData.amount}
              currency={formData.currency}
              stablecoin={formData.stablecoin}
              businessView={mode === 'business'}
              showConfiguration={mode === 'business'}
              onConfigurationChange={(rail, config) => {
                console.log(`Settlement configuration updated for ${rail}:`, config)
                // In a real app, this would save to backend
              }}
            />
          </div>
        </div>

        {/* Cost Calculation Section */}
        {costCalculation && (
          <div className="professional-card">
            <div className="professional-card__header">
              <div className="flex items-center gap-3">
                <div className="professional-demo__feature-icon professional-demo__feature-icon--green">
                  <Calculator className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="professional-card__title">Cost Breakdown</h3>
                  <p className="professional-card__subtitle">
                    {formData.paymentMethod === 'on_ramp_purchase' ? 'Total cost including stablecoin purchase' : 'Real-time cost calculation with savings'}
                  </p>
                </div>
              </div>
              {calculating && (
                <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
              )}
            </div>
            <div className="professional-card__content">
              <div className="enhanced-payment-interface__cost-breakdown">
                {formData.paymentMethod === 'on_ramp_purchase' && costCalculation.onRampCost && (
                  <>
                    <div className="enhanced-payment-interface__cost-section">
                      <div className="enhanced-payment-interface__cost-section-title">Stablecoin Purchase</div>
                      <div className="enhanced-payment-interface__cost-row">
                        <span>Purchase amount:</span>
                        <span>{formData.currency} {costCalculation.onRampCost.toFixed(2)}</span>
                      </div>
                      <div className="enhanced-payment-interface__cost-row">
                        <span>On-ramp fees:</span>
                        <span>{formData.currency} {(costCalculation.onRampFee || 0).toFixed(2)}</span>
                      </div>
                      <div className="enhanced-payment-interface__cost-row">
                        <span>You will receive:</span>
                        <span className="enhanced-payment-interface__cost-value">
                          {costCalculation.stablecoinAmount.toFixed(6)} {formData.stablecoin}
                        </span>
                      </div>
                    </div>
                    <div className="enhanced-payment-interface__cost-divider"></div>
                  </>
                )}
                
                <div className="enhanced-payment-interface__cost-section">
                  <div className="enhanced-payment-interface__cost-section-title">Payment Processing</div>
                  <div className="enhanced-payment-interface__cost-row">
                    <span>{mode === 'customer' ? 'You will pay:' : 'Customer pays:'}</span>
                    <span className="enhanced-payment-interface__cost-value">
                      {costCalculation.stablecoinAmount.toFixed(6)} {formData.stablecoin}
                    </span>
                  </div>
                  <div className="enhanced-payment-interface__cost-row">
                    <span>Exchange rate:</span>
                    <span>1 {formData.stablecoin} = {costCalculation.exchangeRate.toFixed(4)} {formData.currency}</span>
                  </div>
                  <div className="enhanced-payment-interface__cost-row">
                    <span>FX spread:</span>
                    <span>{formData.currency} {costCalculation.fxCost.toFixed(2)}</span>
                  </div>
                  <div className="enhanced-payment-interface__cost-row">
                    <span>Processing fee:</span>
                    <span>{formData.currency} {costCalculation.processingFee.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="enhanced-payment-interface__cost-divider"></div>
                
                <div className="enhanced-payment-interface__cost-row enhanced-payment-interface__cost-row--total">
                  <span>Total cost:</span>
                  <span className="enhanced-payment-interface__cost-value">
                    {formData.currency} {(formData.paymentMethod === 'on_ramp_purchase' && costCalculation.totalWithOnRamp ? 
                      costCalculation.totalWithOnRamp : costCalculation.totalCost).toFixed(2)}
                  </span>
                </div>
                
                {/* Savings Highlight */}
                <div className="enhanced-payment-interface__savings-highlight">
                  <div className="enhanced-payment-interface__savings-header">
                    <TrendingDown className="w-5 h-5 text-green-600" />
                    <span className="enhanced-payment-interface__savings-title">
                      Savings vs Traditional Transfer
                    </span>
                  </div>
                  <div className="enhanced-payment-interface__savings-comparison">
                    <div className="enhanced-payment-interface__savings-item">
                      <span>Traditional cost:</span>
                      <span className="line-through text-gray-500">
                        {formData.currency} {costCalculation.traditionalCost.toFixed(2)}
                      </span>
                    </div>
                    <div className="enhanced-payment-interface__savings-item">
                      <span>StablePay cost:</span>
                      <span className="text-green-600 font-semibold">
                        {formData.currency} {(formData.paymentMethod === 'on_ramp_purchase' && costCalculation.totalWithOnRamp ? 
                          costCalculation.totalWithOnRamp : costCalculation.totalCost).toFixed(2)}
                      </span>
                    </div>
                    <div className="enhanced-payment-interface__savings-total">
                      <span>You save:</span>
                      <span className="enhanced-payment-interface__savings-amount">
                        {formData.currency} {(costCalculation.traditionalCost - 
                          (formData.paymentMethod === 'on_ramp_purchase' && costCalculation.totalWithOnRamp ? 
                            costCalculation.totalWithOnRamp : costCalculation.totalCost)).toFixed(2)}
                        <span className="enhanced-payment-interface__savings-percentage">
                          ({(((costCalculation.traditionalCost - 
                            (formData.paymentMethod === 'on_ramp_purchase' && costCalculation.totalWithOnRamp ? 
                              costCalculation.totalWithOnRamp : costCalculation.totalCost)) / 
                            costCalculation.traditionalCost) * 100).toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Settlement Time */}
                <div className="enhanced-payment-interface__settlement-time">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>
                    Estimated processing time: <strong>
                      {formData.paymentMethod === 'on_ramp_purchase' && onRampQuote ? 
                        `${onRampQuote.estimatedTime} + ${costCalculation.estimatedTime}` : 
                        costCalculation.estimatedTime}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="enhanced-payment-interface__error">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="enhanced-payment-interface__error-dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Submit Button */}
        <div className="enhanced-payment-interface__actions">
          <button
            type="submit"
            disabled={
              !costCalculation || 
              calculating || 
              (mode === 'customer' && formData.paymentMethod === 'on_ramp_purchase' && !onRampQuote) ||
              (mode === 'customer' && formData.paymentMethod === 'existing_wallet' && !formData.isWalletConnected)
            }
            className="professional-btn professional-btn--primary enhanced-payment-interface__submit-btn"
          >
            {calculating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Calculating...
              </>
            ) : formData.paymentMethod === 'on_ramp_purchase' ? (
              <>
                <ExternalLink className="w-4 h-4" />
                Buy Stablecoins & Pay
              </>
            ) : (
              <>
                <ArrowRight className="w-4 h-4" />
                {mode === 'customer' ? 'Pay Invoice' : 'Process Payment'}
              </>
            )}
          </button>
          
          <div className="enhanced-payment-interface__info">
            <Info className="w-4 h-4 text-blue-600" />
            <span>
              {formData.paymentMethod === 'on_ramp_purchase' ? 
                'You will be redirected to complete the stablecoin purchase first' :
                formData.paymentMethod === 'existing_wallet' && !formData.isWalletConnected ?
                'Please connect your wallet to continue' :
                'Your payment will be processed securely on the blockchain'}
            </span>
          </div>
        </div>
      </form>
    </div>
  )
}